<script setup lang="ts">
import type { ReadingPosition } from '~/composables/book/useBookUrl'
import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import type { BookAndArticle, Page } from '~/utils/solr/literature'
import { truthy } from '#shared/filter'
import { useScrollToPosition } from '~/composables/book/useScrollToPosition'
import { useTocState } from '~/composables/book/useTocState'
import { useVisiblePosition } from '~/composables/book/useVisiblePosition'
import { useLiteratureFootnotes } from '~/composables/footnotes/useLiteratureFootnotes'

const props = defineProps<{
  searchResult: { id: string, highlights: string[] } | null
  book: BookAndArticle
  pages: Page[]
  isLoading: boolean
  isFetching: boolean
  canLoadMore: { top: boolean, bottom: boolean }
  isLoadingMore: boolean
  position: ReadingPosition
}>()

const emit = defineEmits<{
  visiblePositionChanged: [position: ReadingPosition, tocEntry: TocItemData | null]
  loadMore: [direction: 'top' | 'bottom', scrollContainer: HTMLElement | null]
}>()

const { flatTocItems } = useTocState()
const containers = inject(diKeys.containers, null)
const scrollContainer = computed(() => containers?.content?.value ?? null)

const scrollToPosition = useScrollToPosition({ container: scrollContainer })

const { scrollToElement } = useScrollToElement('content')

const isNavigating = ref<boolean>(false)
const isScrolling = computed(() => scrollToPosition.isScrolling.value)
const isStable = computed(() => !isNavigating.value && !isScrolling.value)

watch(
  () => props.position,
  async (pos) => {
    isNavigating.value = true
    await scrollToPosition.scrollTo(pos)
    isNavigating.value = false
  },
  { immediate: true },
)

const { visiblePosition, activeTocEntry, observe, unobserve } = useVisiblePosition({
  container: scrollContainer,
  tocEntries: flatTocItems,
})

watch([visiblePosition, activeTocEntry, isStable], ([pos, entry, stable]) => {
  if (!stable) {
    return
  }
  emit('visiblePositionChanged', pos, entry)
}, { deep: true })

watch([() => props.searchResult, isNavigating], ([searchResult, navigating]) => {
  if (!searchResult || navigating) {
    return
  }
  nextTick(() => {
    scrollToElement(searchResult.id, true)
  })
})

function getPageAnchors(page: HTMLElement | null) {
  if (!page) {
    return []
  }
  return Array.from(page.querySelectorAll<HTMLElement>('[data-body] [id]'))
}

function getTocTargetsInPage(page: HTMLElement | null, pageNumber: string) {
  return getPageAnchors(page)
    .map((el) => {
      const tocItem = flatTocItems.value.find(it => it.fragmentId === el.id && it.pageNumberString === pageNumber)
      return tocItem ? { tocItem, element: el } : null
    })
    .filter(truthy)
}

function processPageElements(
  page: HTMLElement | null,
  pageNumber: string,
  action: 'register' | 'unregister',
) {
  const isRegister = action === 'register'

  if (isRegister && page) {
    observe(page)
  }
  else {
    unobserve(page)
  }

  for (const { element } of getTocTargetsInPage(page, pageNumber)) {
    if (isRegister) {
      observe(element)
    }
    else {
      unobserve(element)
    }
  }
}

const hasPages = computed(() => props.pages.length > 0)
const footnotes = useLiteratureFootnotes(() => props.book.id)
</script>

<template>
  <div>
    <div class="max-w-4xl mx-auto md:px-m">
      <div class="sticky top-0 max-md:top-[52px] z-10">
        <div class="flex bg-surface-elevated">
          <div class="xs:max-md:hidden text-caption mr-l mt-3xs shrink-0 w-12">
            <!-- Fixed width placeholder to match page number column -->
          </div>
          <slot name="header" :is-navigating="isNavigating" />
        </div>
      </div>

      <LiteratureLoadMoreButton
        v-if="canLoadMore.top && hasPages"
        direction="top"
        :is-loading="isLoadingMore"
        :disabled="isLoadingMore || isFetching"
        @click="emit('loadMore', 'top', scrollContainer)"
      />

      <LiteraturePage
        v-for="page in pages"
        :key="page.id"
        :data-page-counter="page.pageCounter"
        :page="page"
        :book="book"
        :search-result="searchResult"
        :footnotes="footnotes.data.value"
        @open-footnote="footnotes.open"
        @close-footnote="footnotes.close"
        @mounted="(page, num) => processPageElements(page, num, 'register')"
        @before-unmount="(page, num) => processPageElements(page, num, 'unregister')"
      />

      <LiteratureLoadingState
        :is-loading="isLoading"
        :no-more-results="false"
        :no-results="!hasPages && !isFetching"
        loading-message="Laster sider..."
      />

      <LiteratureLoadMoreButton
        v-if="canLoadMore.bottom && hasPages"
        direction="bottom"
        :is-loading="isFetching"
        :disabled="isFetching"
        @click="emit('loadMore', 'bottom', scrollContainer)"
      />
    </div>
  </div>
</template>
